import { CaptchaResponse, SubmitCaptchaResponse } from "../models/Captcha";
import { CaptchaDetailsForm } from "../models/CaptureDetailsForm";
import {
  Booking,
  CaptureDetailsResponse,
  CreateBookingRequest,
  CreateBookingResponse,
} from "../models/BookingDetails";
import { DropDownOption } from "../models/DropDownOption";
import {
  ActiveReasonsResponse,
  AllAvailableReasonsResponse,
} from "../models/BookingReasons";
import { BranchesResponse } from "../models/Branches";
import {
  AvailableDaysResponse,
  AvailableTimeResponse,
  SlotsRequest,
} from "../models/Slots";
import { ErrorResponse } from "../models/ErrorResponse";
import { ValidateSessionResponse } from "../models/ValidateSession";

const CATCH_ERROR = {
  error: "An unexpected error occurred. Please try again later.",
};

let urlPrefix =
  process.env.NODE_ENV === "production"
    ? process.env.REACT_APP_DIST_PREFIX
    : "";

export const validateSession = (): Promise<ValidateSessionResponse> => {
  let token = sessionStorage.getItem("token") ?? "";
  let agent = false;
  if (!token || token === "") {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    token = urlParams.get("token") ?? "";
    if (token && token !== "") {
      const url = window.location.href.split("?");
      sessionStorage.setItem("token", token);
      window.history.replaceState({}, "SARSeBooking", url[0]);
      agent = true;
    }
  }

  let headers = {};
  if (token && token !== "") {
    headers = { Authorization: `Bearer ${token}` };
  }

  return new Promise((resolve, reject) => {
    fetch(urlPrefix + "/ValidateSession", {
      method: "POST",
      headers: { ...headers },
      body: JSON.stringify({ token, agent, session: sessionStorage }),
    })
      .then((response) => response.json())
      .then((res) => {
        if (res.error) {
          reject(res);
        } else {
          resolve(res);
        }
      })
      .catch((err) => {
        reject(CATCH_ERROR);
      });
  });
};

export const dropdownLookup = (
  token: string = ""
): Promise<Array<DropDownOption>> => {
  const sessionToken = token ? token : sessionStorage.getItem("token");
  return new Promise((resolve, reject) => {
    fetch(urlPrefix + "/DropdownLookup", {
      method: "GET",
      headers: {
        Authorization: `Bearer ${sessionToken}`,
      },
    })
      .then((response) => response.json())
      .then((res) => {
        if (res.error) {
          reject(res);
        } else {
          resolve(res);
        }
      })
      .catch((err) => {
        reject(CATCH_ERROR);
      });
  });
};

export const requestCaptcha = (): Promise<CaptchaResponse> => {
  const token = sessionStorage.getItem("token");
  return new Promise((resolve, reject) => {
    fetch(urlPrefix + "/RequestCaptcha", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) =>
        response.json().then((res) => {
          if (res.error) {
            reject(res);
          } else {
            resolve(res);
          }
        })
      )
      .catch((err) => {
        reject(CATCH_ERROR);
      });
  });
};

export const submitCaptcha = (data: string): Promise<SubmitCaptchaResponse> => {
  const token = sessionStorage.getItem("token");
  return new Promise((resolve, reject) => {
    fetch(urlPrefix + "/SubmitCaptcha", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ captcha: data }),
    })
      .then((response) =>
        response.json().then((res) => {
          if (res.error) {
            reject(res);
          } else {
            resolve(res);
          }
        })
      )
      .catch((err) => {
        reject(CATCH_ERROR);
      });
  });
};

export const submitCaptureDetailsForm = (
  data: CaptchaDetailsForm
): Promise<CaptureDetailsResponse> => {
  const token = sessionStorage.getItem("token");
  return new Promise((resolve, reject) => {
    fetch(urlPrefix + "/SubmitCaptureDetailsForm", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    })
      .then((response) => {
        return response.json();
      })
      .then((res) => {
        if (res.error) {
          reject(res);
        } else {
          resolve(res);
        }
      })
      .catch((err) => {
        console.error(err);
        reject(CATCH_ERROR);
      });
  });
};

export const getAvailableDates = (
  data: SlotsRequest
): Promise<AvailableDaysResponse[]> => {
  const token = sessionStorage.getItem("token");
  return new Promise((resolve, reject) => {
    fetch(urlPrefix + "/AvailableDates", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    })
      .then((response) => {
        return response.json();
      })
      .then((res) => {
        if (res.error) {
          reject(res);
        } else {
          resolve(res);
        }
      })
      .catch((err) => {
        console.error(err);
        reject(CATCH_ERROR);
      });
  });
};

export const getAvailableTimes = (
  data: SlotsRequest
): Promise<AvailableTimeResponse> => {
  const token = sessionStorage.getItem("token");
  return new Promise((resolve, reject) => {
    fetch(urlPrefix + "/AvailableTimes", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    })
      .then((response) => response.json())
      .then((res) => {
        if (res.error) {
          reject(res);
        } else {
          resolve(res);
        }
      })
      .catch((err) => {
        console.error(err);
        reject(CATCH_ERROR);
      });
  });
};

export const branchesLookup = (): Promise<BranchesResponse> => {
  const token = sessionStorage.getItem("token");
  return new Promise((resolve, reject) => {
    fetch(urlPrefix + "/BranchesLookup", {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => response.json())
      .then((res) => {
        if (res.error) {
          reject(res);
        } else {
          resolve(res);
        }
      })
      .catch((err) => {
        reject(CATCH_ERROR);
      });
  });
};

export const activeReasonsLookup = (
  requestorType: string
): Promise<ActiveReasonsResponse> => {
  const token = sessionStorage.getItem("token");
  return new Promise((resolve, reject) => {
    fetch(urlPrefix + "/ActiveReasonsLookup", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ requestorType }),
    })
      .then((response) => response.json())
      .then((res) => {
        if (res.error) {
          reject(res);
        } else {
          resolve(res);
        }
      })
      .catch((err) => {
        reject(CATCH_ERROR);
      });
  });
};

export const availableReasonsLookup = (
  region: string
): Promise<AllAvailableReasonsResponse> => {
  const token = sessionStorage.getItem("token");
  return new Promise((resolve, reject) => {
    fetch(urlPrefix + "/AvailableReasonsLookup", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ region }),
    })
      .then((response) => response.json())
      .then((res) => {
        if (res.error) {
          reject(res);
        } else {
          resolve(res);
        }
      })
      .catch((err) => {
        reject(CATCH_ERROR);
      });
  });
};

export const createBooking = (data: CreateBookingRequest) => {
  const token = sessionStorage.getItem("token");
  //prettier-ignore
  return new Promise<CreateBookingResponse>((resolve, reject) => {
    fetch(urlPrefix + "/CreateBooking", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    })
      .then((response) =>
        response.json().then((res) => {
          if (res.error) {
            reject(res);
          } else {
            resolve(res);
          }
        })
      )
      .catch((err) => {
        console.error(err);
        reject(CATCH_ERROR);
      });
  });
};

export const cancelBookingRequest = (
  cancelReason: string,
  bookingId: number,
  bookingTaxpayerID: number
): Promise<Booking[]> => {
  const token = sessionStorage.getItem("token");
  return new Promise((resolve, reject) => {
    fetch(urlPrefix + "/CancelBooking", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ bookingId, bookingTaxpayerID, cancelReason }),
    })
      .then((response) => {
        return response.json();
      })
      .then((res) => {
        if (res.error) {
          reject(res);
        } else {
          resolve(res);
        }
      })
      .catch((err) => {
        reject(CATCH_ERROR);
      });
  });
};

export const uploadFiles = (files: Array<File>): Promise<ErrorResponse> => {
  const token = sessionStorage.getItem("token");

  const form = new FormData();
  files?.forEach((file: File) => {
    form.append("files", file, file.name);
  });

  return new Promise((resolve, reject) => {
    fetch(urlPrefix + "/UploadFiles", {
      method: "POST",
      credentials: "include",
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    })
      .then((response) => response.json())
      .then((res) => {
        if (res.error) {
          reject(res);
        } else {
          resolve(res);
        }
      })
      .catch((err) => {
        reject(CATCH_ERROR);
      });
  });
};
